import * as vp from 'vopay';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

const { Box, SubTitle, Table, formattedCurrency } = vp;

type TablePropsHandlers = {
  accountDetails: () => vp.TableProps;
  accountBalance: () => vp.TableProps;
};

type KeyValuePair = {
  display: string;
  value: string;
  visible?: boolean;
};

type SubAccountOverviewProps = {
  account: vp.SubAccountsLists;
  currency: string;
};

type FC = FunctionComponent<SubAccountOverviewProps>;

const SubAccountOverview: FC = (props: SubAccountOverviewProps) => {
  const { account, currency } = props;
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.Details' });

  return (
    <Box flx="col" width="50%" mt="lg">
      <SubTitle subTitle={t('AccountDetails.title')} />
      <Box mb="md">
        <Table {...getTableProps('accountDetails')} />
      </Box>
      <SubTitle subTitle={t('AccountBalance.title')} />
      <Box mb="lg">
        <Table {...getTableProps('accountBalance')} />
      </Box>
    </Box>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      accountDetails: () => {
        const accountDetailsPair: KeyValuePair[] = [
          {
            display: t('AccountDetails.legalBusinessName'),
            value: account.LegalBusinessName || '',
            visible: true,
          },
          {
            display: t('AccountDetails.accountName'),
            value: account.AccountName || '',
            visible: true,
          },
          {
            display: t('AccountDetails.accountID'),
            value: account.AccountID || '',
            visible: true,
          },
          {
            display: t('AccountDetails.email'),
            value: account.Email || '',
            visible: true,
          },
          {
            display: t('AccountDetails.phone'),
            value: account.Phone || '',
            visible: account.Phone !== '',
          },
          {
            display: t('AccountDetails.city'),
            value: account.City || '',
            visible: account.City !== '',
          },
          {
            display: t('AccountDetails.province'),
            value: account.Province || '',
            visible: account.Province !== '',
          },
          {
            display: t('AccountDetails.postalCode'),
            value: account.PostalCode || '',
            visible: account.PostalCode !== '',
          },
          {
            display: t('AccountDetails.subAccountType'),
            value: vp.capitalizeFirstLetter(account.SubAccountType || ''),
            visible: account.SubAccountType !== '',
          },
        ];

        const subAccountDetails: vp.TableProps = {
          name: 'account-details',
          variant: 'pair',
          columns: {
            detailKey: { header: 'account Detail' },
            detailValue: { header: '' },
          },
          rows: accountDetailsPair
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return subAccountDetails;
      },
      accountBalance: () => {
        const balance = Object.values(account.Balances).find((x) => x.Currency === currency);
        const clientDetailsPair: KeyValuePair[] = [
          {
            display: t('AccountBalance.currency'),
            value: `${balance?.Currency || ''}`,
          },
          {
            display: t('AccountBalance.accountBalance'),
            value: `$${formattedCurrency(balance?.AccountBalance || '')}`,
          },
          {
            display: t('AccountBalance.pendingBalance'),
            value: `$${formattedCurrency(balance?.PendingBalance || '')}`,
          },
          {
            display: t('AccountBalance.reserve'),
            value: `$${formattedCurrency(balance?.Reserve || '')}`,
          },
          {
            display: t('AccountBalance.availableBalance'),
            value: `$${formattedCurrency(balance?.AvailableBalance || '')}`,
          },
        ];

        const subAccountDetails: vp.TableProps = {
          name: 'account-balance',
          variant: 'pair',
          columns: {
            detailKey: { header: 'Account Balance' },
            detailValue: { header: '' },
          },
          rows: clientDetailsPair.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return subAccountDetails;
      },
    };

    return handlers[handler]();
  }
};

export default SubAccountOverview;
